import React, {useState, useEffect} from 'react';
import { avatarColor, Icon } from '../shared';

// The server gives up on a waiting customer after 2 minutes (sweep.py) and the call moves to Missed calls.
const WAIT_LIMIT = 120;

const waitedSeconds = (requestedAt) => {
  if (!requestedAt) return 0;
  const iso = /Z|[+-]\d\d:?\d\d$/.test(requestedAt) ? requestedAt : `${requestedAt}Z`;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
};

export function IncomingCallModal({ caller, waitingCount = 1, accepting, error, onAccept, onDecline }) {
  const [seconds, setSeconds] = useState(() => waitedSeconds(caller.requestedAt));
  const AUTO_DISMISS = WAIT_LIMIT;

  useEffect(() => {
    setSeconds(waitedSeconds(caller.requestedAt));
    const t = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [caller.requestId, caller.requestedAt]);

  const pct  = Math.min((seconds / AUTO_DISMISS) * 100, 100);
  const fmt  = s => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  const { bg, fg } = avatarColor(caller.initials);

  return (
      <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Incoming video call">
        <div className="incoming-call-card">
          <div className="autodismiss-bar" style={{ width: `${100 - pct}%` }} />

          <button className="close-modal-btn" onClick={onDecline} aria-label="Decline call"><Icon.X /></button>

          <div className="incoming-label">
            <Icon.Video />
            Incoming Video Call
          </div>

          <div className="caller-avatar-wrap">
            <div className="caller-ring"  aria-hidden="true" />
            <div className="caller-ring2" aria-hidden="true" />
            {caller.photoUrl ? (
                <img
                    src={caller.photoUrl}
                    alt={caller.name}
                    className="caller-avatar-img"
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                />
            ) : null}
            <div className="caller-avatar-circle" style={{ background: bg, color: fg, display: caller.photoUrl ? 'none' : 'flex' }}>
              {caller.initials}
            </div>
          </div>

          <div className="caller-name">{caller.name}</div>
          <div className="caller-property">{caller.property}</div>
          {caller.mobile && <div className="caller-mobile">{caller.mobile}</div>}

          {waitingCount > 1 && (
              <div className="caller-queue-badge">
                {waitingCount - 1} more waiting after this one
              </div>
          )}

          <div className="call-timer-display">
            <div className="timer-dots" aria-hidden="true">
              {[0, 0.15, 0.3].map((d, i) => <div key={i} className="timer-dot" style={{ animationDelay: `${d}s` }} />)}
            </div>
            <span className="timer-text" aria-live="polite">{fmt(seconds)}</span>
            <div className="timer-dots" aria-hidden="true">
              {[0, 0.15, 0.3].map((d, i) => <div key={i} className="timer-dot" style={{ animationDelay: `${d}s` }} />)}
            </div>
          </div>

          <p className="autodismiss-hint">
            {seconds < AUTO_DISMISS ? `Waiting ${seconds}s — moves to Missed calls after 2 min` : 'About to move to Missed calls…'}
          </p>
          {error && <p className="incoming-error" role="alert">{error}</p>}

          <div className="call-action-btns">
            <button className="btn-decline" onClick={onDecline} disabled={accepting}>
              <Icon.PhoneOff /> Not now
            </button>
            <button className="btn-accept" onClick={onAccept} disabled={accepting}>
              <Icon.Video /> {accepting ? 'Connecting…' : 'Accept'}
            </button>
          </div>
        </div>
      </div>
  );
}
